import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';
import AppSidebar from '../components/AppSidebar';
import {
  BarChart3,
  DollarSign,
  Zap,
  TrendingUp,
  CheckCircle2,
  XCircle,
  LogOut,
  FolderOpen,
  Settings,
  Home,
  Users,
  Activity,
  FileText,
  Clock,
  ArrowLeft,
  Library,
  Search
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sidebar (shared pattern)

// Stat card component
const StatCard = ({ icon: Icon, label, value, subValue, color, testId }) => (
  <Card className="border-slate-200 overflow-hidden" data-testid={testId}>
    <div className={`h-1.5 ${color}`}></div>
    <CardContent className="p-5">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color.replace('bg-gradient-to-r from-', 'bg-').split(' ')[0]}/10`}>
          <Icon className={`w-6 h-6 ${color.replace('bg-gradient-to-r from-', 'text-').split(' ')[0]}`} />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-sm text-slate-500">{label}</p>
          {subValue && <p className="text-xs text-slate-400 mt-0.5">{subValue}</p>}
        </div>
      </div>
    </CardContent>
  </Card>
);

// Action label mapping
const ACTION_LABELS = {
  summarize: 'Summarization',
  research: 'Legal Research',
  draft_reply: 'Draft Reply',
  draft_summary_for_management: 'Management Summary',
  draft_legal_brief: 'Legal Brief',
  draft_writ_petition: 'Writ Petition',
  draft_scn_reply_cgst_sgst: 'SCN Reply (CGST/SGST)',
  draft_gst_appeal: 'GST Appeal',
  draft_retraction_statement: 'Statement Retraction',
  draft_adjournment_letter: 'Adjournment Letter'
};

const formatAction = (action) => ACTION_LABELS[action] || action.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const UsageDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, logsRes] = await Promise.all([
        axios.get(`${API}/admin/usage/summary`),
        axios.get(`${API}/admin/usage/logs?limit=50`)
      ]);
      setSummary(summaryRes.data);
      setLogs(logsRes.data.logs);
    } catch (error) {
      console.error('Error fetching usage data:', error);
      toast.error('Failed to load usage data');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-[#1E3A8A]/20 border-t-[#1E3A8A] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading usage data...</p>
        </div>
      </div>
    );
  }

  const s = summary || {};
  const actionEntries = Object.entries(s.action_breakdown || {}).sort((a, b) => b[1].cost - a[1].cost);
  const modelEntries = Object.entries(s.model_breakdown || {});
  const userEntries = Object.entries(s.user_breakdown || {}).sort((a, b) => b[1].cost - a[1].cost);
  const dailyEntries = Object.entries(s.daily_breakdown || {}).sort((a, b) => a[0].localeCompare(b[0])).slice(-14);

  // Monthly projection: take daily avg from available data and multiply by 30
  const totalDays = Object.keys(s.daily_breakdown || {}).length || 1;
  const dailyAvgCost = (s.total_estimated_cost || 0) / totalDays;
  const monthlyProjection = dailyAvgCost * 30;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <AppSidebar user={user} onLogout={logout} active="usage" />
      <main className="ml-72 p-8" data-testid="usage-dashboard">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button variant="ghost" onClick={() => navigate('/admin')} className="mb-4 hover:bg-slate-100" data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Admin
            </Button>
            <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="usage-title">
              Usage & Cost Tracking
            </h1>
            <p className="text-slate-500 mt-1">Monitor AI API calls, token usage, and estimated costs</p>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="usage-stats">
            <StatCard
              icon={Zap}
              label="Total API Calls"
              value={s.total_calls || 0}
              subValue={`${s.successful_calls || 0} successful, ${s.failed_calls || 0} failed`}
              color="bg-gradient-to-r from-blue-500 to-blue-600"
              testId="stat-total-calls"
            />
            <StatCard
              icon={DollarSign}
              label="Total Estimated Cost"
              value={`$${(s.total_estimated_cost || 0).toFixed(4)}`}
              subValue={`~$${monthlyProjection.toFixed(2)}/month projected`}
              color="bg-gradient-to-r from-green-500 to-green-600"
              testId="stat-total-cost"
            />
            <StatCard
              icon={TrendingUp}
              label="Input Tokens"
              value={(s.total_input_tokens || 0).toLocaleString()}
              subValue="Total tokens sent to LLMs"
              color="bg-gradient-to-r from-purple-500 to-purple-600"
              testId="stat-input-tokens"
            />
            <StatCard
              icon={Activity}
              label="Output Tokens"
              value={(s.total_output_tokens || 0).toLocaleString()}
              subValue="Total tokens generated"
              color="bg-gradient-to-r from-amber-500 to-amber-600"
              testId="stat-output-tokens"
            />
          </div>

          {/* Two Column Layout */}
          <div className="grid lg:grid-cols-2 gap-6 mb-8">
            {/* Cost by Action Type */}
            <Card className="border-slate-200" data-testid="action-breakdown">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#1E3A8A]" />
                  Cost by Action Type
                </CardTitle>
                <CardDescription>Breakdown of spend per AI feature</CardDescription>
              </CardHeader>
              <CardContent>
                {actionEntries.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-8">No usage data yet. Costs will appear here after you use AI features.</p>
                ) : (
                  <div className="space-y-3">
                    {actionEntries.map(([action, data]) => {
                      const maxCost = Math.max(...actionEntries.map(([, d]) => d.cost));
                      const pct = maxCost > 0 ? (data.cost / maxCost) * 100 : 0;
                      return (
                        <div key={action} data-testid={`action-${action}`}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-slate-700">{formatAction(action)}</span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-slate-400">{data.count} calls</span>
                              <span className="text-sm font-semibold text-slate-900">${data.cost.toFixed(4)}</span>
                            </div>
                          </div>
                          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] rounded-full transition-all" style={{ width: `${pct}%` }}></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cost by Model + User */}
            <div className="space-y-6">
              {/* Model Breakdown */}
              <Card className="border-slate-200" data-testid="model-breakdown">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Zap className="w-5 h-5 text-[#F59E0B]" />
                    Cost by Model
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {modelEntries.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No data yet</p>
                  ) : (
                    <div className="space-y-3">
                      {modelEntries.map(([model, data]) => (
                        <div key={model} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`model-${model}`}>
                          <div>
                            <p className="font-semibold text-slate-900 text-sm">{model}</p>
                            <p className="text-xs text-slate-500">{data.count} calls &middot; {data.input_tokens.toLocaleString()} in / {data.output_tokens.toLocaleString()} out tokens</p>
                          </div>
                          <span className="text-lg font-bold text-[#1E3A8A]">${data.cost.toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* User Breakdown */}
              <Card className="border-slate-200" data-testid="user-breakdown">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Users className="w-5 h-5 text-purple-500" />
                    Cost by User
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {userEntries.length === 0 ? (
                    <p className="text-sm text-slate-400 text-center py-4">No data yet</p>
                  ) : (
                    <div className="space-y-2">
                      {userEntries.map(([email, data]) => (
                        <div key={email} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`user-${email}`}>
                          <div>
                            <p className="font-medium text-slate-900 text-sm">{email}</p>
                            <p className="text-xs text-slate-500">{data.count} API calls</p>
                          </div>
                          <span className="font-semibold text-slate-700">${data.cost.toFixed(4)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Daily Trend */}
          {dailyEntries.length > 0 && (
            <Card className="border-slate-200 mb-8" data-testid="daily-trend">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                  Daily Cost Trend (Last 14 Days)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-1 h-40">
                  {dailyEntries.map(([day, data]) => {
                    const maxDailyCost = Math.max(...dailyEntries.map(([, d]) => d.cost));
                    const heightPct = maxDailyCost > 0 ? (data.cost / maxDailyCost) * 100 : 5;
                    return (
                      <div key={day} className="flex-1 flex flex-col items-center gap-1 group" title={`${day}: $${data.cost.toFixed(4)} (${data.count} calls)`}>
                        <span className="text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">${data.cost.toFixed(3)}</span>
                        <div
                          className="w-full bg-gradient-to-t from-[#1E3A8A] to-[#3B82F6] rounded-t-sm min-h-[4px] transition-all group-hover:from-[#F59E0B] group-hover:to-[#FBBF24]"
                          style={{ height: `${Math.max(heightPct, 3)}%` }}
                        ></div>
                        <span className="text-[9px] text-slate-400 -rotate-45 origin-top-left whitespace-nowrap">{day.slice(5)}</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Logs */}
          <Card className="border-slate-200" data-testid="usage-logs">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-500" />
                Recent API Calls
              </CardTitle>
              <CardDescription>Detailed log of all AI API usage</CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[400px]">
                {logs.length === 0 ? (
                  <div className="text-center py-12">
                    <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No API calls recorded yet</p>
                    <p className="text-sm text-slate-400 mt-1">Usage logs will appear here when you use AI features</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {logs.map((log) => (
                      <div
                        key={log.log_id}
                        className={`flex items-center gap-4 p-3 rounded-lg border ${log.success ? 'bg-white border-slate-100' : 'bg-red-50 border-red-100'}`}
                        data-testid={`log-${log.log_id}`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${log.success ? 'bg-green-100' : 'bg-red-100'}`}>
                          {log.success ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-red-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm text-slate-900">{formatAction(log.action)}</span>
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{log.model}</span>
                          </div>
                          <p className="text-xs text-slate-500 truncate">{log.user_email} &middot; {log.input_tokens?.toLocaleString()} in / {log.output_tokens?.toLocaleString()} out tokens</p>
                          {!log.success && log.error_message && (
                            <p className="text-xs text-red-600 mt-0.5 truncate">{log.error_message}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="font-semibold text-sm text-slate-900">${log.estimated_cost?.toFixed(4)}</p>
                          <p className="text-[10px] text-slate-400">{new Date(log.created_at).toLocaleString()}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Cost Estimation Help */}
          <Card className="border-slate-200 mt-8 bg-blue-50/50" data-testid="cost-help">
            <CardContent className="p-6">
              <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#1E3A8A]" />
                Monthly Cost Estimation Guide
              </h3>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="font-medium text-slate-900 mb-1">Light Usage</p>
                  <p className="text-lg font-bold text-[#1E3A8A]">~$2-5/mo</p>
                  <p className="text-xs text-slate-500 mt-1">~20 docs, 1 summary + 1 draft each</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="font-medium text-slate-900 mb-1">Moderate Usage</p>
                  <p className="text-lg font-bold text-[#F59E0B]">~$10-20/mo</p>
                  <p className="text-xs text-slate-500 mt-1">~50 docs, summary + 2-3 drafts + research each</p>
                </div>
                <div className="bg-white rounded-lg p-4 border border-slate-200">
                  <p className="font-medium text-slate-900 mb-1">Heavy Usage</p>
                  <p className="text-lg font-bold text-red-500">~$30-50/mo</p>
                  <p className="text-xs text-slate-500 mt-1">~100+ docs with full workflow per doc</p>
                </div>
              </div>
              <p className="text-xs text-slate-400 mt-3">* Estimates based on GPT-5.2 pricing. Actual costs depend on document size and prompt complexity. Perplexity research calls billed separately.</p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default UsageDashboard;
